'use client';

import { useAtomValue } from 'jotai';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { IconButton } from '@flipster/react/design-system/icon-button';
import ArrowLeftIcon from '@flipster/react/icon/generated/ArrowLeftIcon';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { Styles, css } from '@flipster/styled-system/css';

import { relatedContentsAtom } from '../jotai';
import RelatedArticle from './related-article';

const RelatedContentsSection = ({ css: cssProp }: { css: Styles }) => {
  const relatedContents = useAtomValue(relatedContentsAtom);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardContainerRef = useRef<HTMLUListElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const updateButtons = () => {
    const scrollContainerEl = scrollContainerRef.current;
    const cardContainerEl = cardContainerRef.current;
    if (!scrollContainerEl || !cardContainerEl) return;
    setHasPrev(scrollContainerEl.scrollLeft !== 0);
    setHasNext(
      scrollContainerEl.scrollLeft + cardContainerEl.offsetWidth + cardContainerEl.offsetLeft + 20 <
        cardContainerEl.scrollWidth,
    );
  };

  const handleScroll = useCallback(() => {
    clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(updateButtons, 200);
  }, []);

  useLayoutEffect(() => {
    setTimeout(() => handleScroll(), 100);
  }, [handleScroll]);

  const scrollToPrev = useCallback(() => {
    const scrollContainerEl = scrollContainerRef.current;
    const cardContainerEl = cardContainerRef.current;
    if (scrollContainerEl && cardContainerEl) {
      const scrollTargetEl = Array.from(cardContainerEl.children)
        .reverse()
        .filter((element): element is HTMLElement => element.nodeType === Node.ELEMENT_NODE)
        .find((element) => element.offsetLeft - cardContainerEl.offsetLeft + 20 < scrollContainerEl.scrollLeft);
      if (!scrollTargetEl) return;
      scrollContainerEl.scrollTo({
        left: scrollTargetEl.offsetLeft - cardContainerEl.offsetLeft,
        behavior: 'smooth',
      });
    }
  }, []);

  const scrollToNext = useCallback(() => {
    const scrollContainerEl = scrollContainerRef.current;
    const cardContainerEl = cardContainerRef.current;
    if (scrollContainerEl && cardContainerEl) {
      const scrollTargetEl = Array.from(cardContainerEl.children)
        .filter((element): element is HTMLElement => element.nodeType === Node.ELEMENT_NODE)
        .find((element) => element.offsetLeft - cardContainerEl.offsetLeft - 20 > scrollContainerEl.scrollLeft);
      if (!scrollTargetEl) return;
      scrollContainerEl.scrollTo({
        left: scrollTargetEl.offsetLeft - cardContainerEl.offsetLeft,
        behavior: 'smooth',
      });
    }
  }, []);

  return (
    <section
      className={css(cssProp, { display: 'grid', gridTemplateColumns: 'inherit', rowGap: 20, tablet: { rowGap: 32 } })}
    >
      <h2
        className={css({
          textStyle: 'Legacy/H4',
          gridColumn: 'container',
          color: 'legacy.gray.e',
          tablet: { textStyle: 'Legacy/H3', gridRow: 1 },
        })}
      >
        <FormattedMessage id='Autogen_common.r251126.s_Related_content' />
      </h2>
      {relatedContents.length > 0 ? (
        <div
          className={css({
            display: 'grid',
            gridTemplateColumns: 'inherit',
            gridColumn: 'full',
            overflowX: 'scroll',
            overscrollBehaviorX: 'none',
            scrollbarWidth: 'none',
          })}
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          <ul
            className={css({
              display: 'grid',
              gridTemplateRows: 'repeat(3, 190px)',
              gridColumn: 'container',
              gridAutoColumns: '100%',
              gridAutoFlow: 'column',
              gap: 20,
              tablet: { gridTemplateRows: 200, gridAutoColumns: 440, gap: 32 },
              '&::after': {
                display: 'block',
                w: 1,
                h: '100%',
                content: ' ',
              },
            })}
            ref={cardContainerRef}
          >
            {relatedContents.map((relatedGlossaryTerm) => (
              <li key={relatedGlossaryTerm.slug}>
                <RelatedArticle article={relatedGlossaryTerm} />
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className={css({ textStyle: 'Legacy/Body2', gridColumn: 'container', color: 'legacy.gray.8' })}>
          <FormattedMessage id='Autogen_common.r251126.s_There_is_no_related_content' />
        </p>
      )}
      <div
        className={css({
          display: 'flex',
          gridColumn: 'container',
          gap: 20,
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          tablet: { gridRow: 1 },
        })}
      >
        <IconButton
          icon={<ArrowLeftIcon />}
          hierarchy='quaternary'
          onClick={scrollToPrev}
          disabled={!hasPrev}
          aria-hidden
        />
        <IconButton
          icon={<ArrowRightIcon />}
          hierarchy='quaternary'
          onClick={scrollToNext}
          disabled={!hasNext}
          aria-hidden
        />
      </div>
    </section>
  );
};

export default RelatedContentsSection;
